import { sanitizeGraph } from "./route-graph.ts";
import type { PieceGraph } from "./types.ts";

export const MAX_PIECE_NAME = 80;
export const MAX_PIECE_JSON_BYTES = 1_000_000;

export interface UserPieceMeta {
  id: string;
  name: string;
  updated_at: string;
  /** True after Publish. The Piece can never be edited again. */
  published?: boolean;
  /** Public template id when published. Same as `id` when published with the current API. */
  template_id?: string | null;
  /** Present when the row is only in IndexedDB. */
  local_only?: boolean;
}

export interface UserPieceDetail extends UserPieceMeta {
  data: UserPieceData;
}

export interface UserPieceData {
  graph: PieceGraph;
}

export interface UserPieceListResponse {
  pieces: UserPieceMeta[];
}

export function normalizePieceName(value: unknown): string {
  if (typeof value !== "string") throw new Error("A name is required.");
  const name = value.trim();
  if (!name) throw new Error("A name is required.");
  if (name.length > MAX_PIECE_NAME) {
    throw new Error(`A name must be ${MAX_PIECE_NAME} characters or fewer.`);
  }
  return name;
}

export function parseUserPieceData(value: unknown): UserPieceData {
  if (!value || typeof value !== "object") {
    throw new Error("Drawing data isn't valid.");
  }
  const raw = value as { graph?: unknown };
  if (!raw.graph || typeof raw.graph !== "object") {
    throw new Error("The points and lines aren't valid.");
  }
  const graphRaw = raw.graph as {
    points?: unknown;
    segments?: unknown;
    start?: unknown;
    workArea?: unknown;
    regions?: unknown;
    pointStyles?: unknown;
    lineStyles?: unknown;
    decorative?: unknown;
    styleDefaults?: unknown;
  };
  if (!Array.isArray(graphRaw.points) || !Array.isArray(graphRaw.segments)) {
    throw new Error("The points and lines aren't valid.");
  }
  const points: Array<[number, number]> = [];
  for (const point of graphRaw.points) {
    if (!Array.isArray(point) || point.length < 2) {
      throw new Error("The points and lines aren't valid.");
    }
    const lng = Number(point[0]);
    const lat = Number(point[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      throw new Error("The points and lines aren't valid.");
    }
    points.push([lng, lat]);
  }
  const segments: Array<[number, number]> = [];
  for (const seg of graphRaw.segments) {
    if (!Array.isArray(seg) || seg.length < 2) {
      throw new Error("The points and lines aren't valid.");
    }
    const a = Number(seg[0]);
    const b = Number(seg[1]);
    if (!Number.isInteger(a) || !Number.isInteger(b)) {
      throw new Error("The points and lines aren't valid.");
    }
    segments.push([a, b]);
  }
  const start = typeof graphRaw.start === "number" ? graphRaw.start : null;
  return {
    graph: sanitizeGraph({
      points,
      segments,
      start,
      workArea: graphRaw.workArea,
      regions: graphRaw.regions,
      pointStyles: graphRaw.pointStyles,
      lineStyles: graphRaw.lineStyles,
      decorative: graphRaw.decorative,
      styleDefaults: graphRaw.styleDefaults,
    }),
  };
}

export function encodedPieceSize(data: UserPieceData): number {
  return new TextEncoder().encode(JSON.stringify(data)).length;
}

export function assertPieceSize(data: UserPieceData): void {
  if (encodedPieceSize(data) > MAX_PIECE_JSON_BYTES) {
    throw new Error(`A drawing can be at most ${MAX_PIECE_JSON_BYTES} bytes.`);
  }
}

export function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}
